/*
 * DossierRow — one row in the dossier panel.
 *
 * Three implicit variants based on state:
 *   - empty           ○  no value yet
 *   - ai_guessed      ◐  value from extractor, awaiting user confirmation
 *   - user_confirmed  ●  user has approved or directly entered
 *
 * Clicking the row opens an inline editor scoped to the field's kind.
 * Pressing return saves, escape cancels.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, X, Pencil } from 'lucide-react';

import type { FieldDef, FieldState, FieldStateEntry } from '../lib/dossier-types';
import { fmtDate } from '../../components/dossier/fieldKit';

export interface DossierRowProps {
  field: FieldDef;
  value: unknown;
  stateEntry?: FieldStateEntry;
  isActive: boolean;          // currently asked-about by the Inquirer
  onCommit: (value: unknown, action: 'confirm' | 'correct') => void;
  // Optional secondary line (e.g., industry → industry_detail). Renders
  // under the main value as a small italic-serif descriptor.
  secondary?: string | null;
}

// Fallback label for a stored value with no matching option — notably the
// 'other' we snap free-typed off-menu answers to. Renders "Other" rather than
// a bare lowercase token; any other unknown value shows as-is.
function labelFallback(v: unknown): string {
  const s = String(v);
  return s === 'other' ? 'Other' : s;
}

function valuePreview(field: FieldDef, value: unknown): string {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) return '';
  if (field.kind === 'multiselect') {
    const list = Array.isArray(value)
      ? value
      : typeof value === 'string'
        ? [value]
        : [];
    return list
      .map(v => field.options?.find(o => o.value === v)?.label ?? labelFallback(v))
      .join(', ');
  }
  if ((field.kind === 'select' || field.kind === 'chip') && typeof value === 'string') {
    return field.options?.find(o => o.value === value)?.label ?? labelFallback(value);
  }
  if (field.kind === 'date') return fmtDate(value);
  return String(value);
}

export function DossierRow({ field, value, stateEntry, isActive, onCommit, secondary }: DossierRowProps) {
  const state: FieldState = stateEntry?.state ?? 'empty';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<unknown>(value);

  useEffect(() => { setDraft(value); }, [value]);

  const startEdit = useCallback(() => {
    setDraft(value);
    setEditing(true);
  }, [value]);

  const cancel = useCallback(() => {
    setDraft(value);
    setEditing(false);
  }, [value]);

  const save = useCallback(() => {
    // Treat editing-from-empty as confirm, editing-from-anything-else as correct.
    const action: 'confirm' | 'correct' = state === 'empty' ? 'confirm' : 'correct';
    onCommit(draft, action);
    setEditing(false);
  }, [draft, onCommit, state]);

  const marker =
    state === 'empty' ? '○'
    : state === 'ai_guessed' ? '◐'
    : state === 'skipped' ? '⌀'
    : '●';
  const preview = useMemo(() => valuePreview(field, value), [field, value]);

  return (
    <div
      className={
        `aa-dossier-row aa-dossier-row--${state}` +
        (isActive ? ' aa-dossier-row--active' : '') +
        (field.critical ? ' aa-dossier-row--critical' : '')
      }
      onClick={editing ? undefined : startEdit}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (!editing && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); startEdit(); } }}
    >
      <div className="aa-dossier-marker" aria-hidden="true">{marker}</div>
      <div className="aa-dossier-body">
        <div className="aa-dossier-label">
          {field.critical && (
            <span className="aa-dossier-crit-star" title="Critical — drives routing decision">★</span>
          )}
          {field.label}
          {field.required_for_routing && state === 'empty' && (
            <span className="aa-dossier-required-pip" title="Needed to lock routing" />
          )}
        </div>
        {!editing && (
          <div className="aa-dossier-value">
            {preview ? (
              <>
                <span>{preview}</span>
                {secondary && (
                  <span className="aa-dossier-secondary">{secondary}</span>
                )}
                {state === 'ai_guessed' && stateEntry && stateEntry.confidence != null && (
                  <span className="aa-dossier-meta">
                    AI · {Math.round(stateEntry.confidence * 100)}% · {stateEntry.source ?? 'unknown'}
                  </span>
                )}
              </>
            ) : (
              <span className="aa-dossier-placeholder">{field.placeholder ?? 'Tap to fill'}</span>
            )}
          </div>
        )}
        {editing && (
          <RowEditor
            field={field}
            draft={draft}
            setDraft={setDraft}
            onSave={save}
            onCancel={cancel}
          />
        )}
      </div>
      {!editing && state !== 'empty' && (
        <Pencil size={14} className="aa-dossier-edit-icon" aria-hidden="true" />
      )}
      {editing && (
        <div className="aa-dossier-actions" onClick={e => e.stopPropagation()}>
          <button type="button" className="aa-dossier-iconbtn" onClick={save} aria-label="Save">
            <Check size={14} />
          </button>
          <button type="button" className="aa-dossier-iconbtn" onClick={cancel} aria-label="Cancel">
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}


// ── Inline editor (kind-aware) ─────────────────────────────────────────

interface RowEditorProps {
  field: FieldDef;
  draft: unknown;
  setDraft: (v: unknown) => void;
  onSave: () => void;
  onCancel: () => void;
}

function RowEditor({ field, draft, setDraft, onSave, onCancel }: RowEditorProps) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleKey = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !(field.kind === 'longtext' && !e.metaKey && !e.ctrlKey)) {
      e.preventDefault();
      onSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  }, [field.kind, onSave, onCancel]);

  if (field.kind === 'longtext') {
    return (
      <textarea
        ref={inputRef as React.MutableRefObject<HTMLTextAreaElement>}
        className="aa-dossier-textarea"
        value={(draft as string) ?? ''}
        placeholder={field.placeholder ?? ''}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={handleKey}
        rows={3}
        onClick={e => e.stopPropagation()}
      />
    );
  }

  if (field.kind === 'select' || field.kind === 'chip') {
    return (
      <div className="aa-dossier-chiprow" onClick={e => e.stopPropagation()}>
        {(field.options ?? []).map(o => (
          <button
            type="button"
            key={o.value}
            className={`aa-chip${draft === o.value ? ' aa-chip--on' : ''}`}
            onClick={() => { setDraft(o.value); }}
          >
            {o.label}
          </button>
        ))}
      </div>
    );
  }

  if (field.kind === 'multiselect') {
    const list = Array.isArray(draft)
      ? (draft as string[])
      : typeof draft === 'string'
        ? [draft]
        : [];
    return (
      <div className="aa-dossier-chiprow" onClick={e => e.stopPropagation()}>
        {(field.options ?? []).map(o => {
          const on = list.includes(o.value);
          return (
            <button
              type="button"
              key={o.value}
              className={`aa-chip${on ? ' aa-chip--on' : ''}`}
              onClick={() => {
                setDraft(on ? list.filter(v => v !== o.value) : [...list, o.value]);
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    );
  }

  if (field.kind === 'number') {
    return (
      <input
        ref={inputRef as React.MutableRefObject<HTMLInputElement>}
        type="number"
        className="aa-dossier-input"
        value={(draft as number | string) ?? ''}
        placeholder={field.placeholder ?? ''}
        onChange={e => setDraft(e.target.value === '' ? '' : Number(e.target.value))}
        onKeyDown={handleKey}
        onClick={e => e.stopPropagation()}
      />
    );
  }

  if (field.kind === 'date') {
    return (
      <input
        ref={inputRef as React.MutableRefObject<HTMLInputElement>}
        type="date"
        className="aa-dossier-input"
        value={typeof draft === 'string' ? draft.slice(0, 10) : ''}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={handleKey}
        onClick={e => e.stopPropagation()}
      />
    );
  }

  return (
    <input
      ref={inputRef as React.MutableRefObject<HTMLInputElement>}
      type={field.kind === 'url' ? 'url' : 'text'}
      className="aa-dossier-input"
      value={(draft as string) ?? ''}
      placeholder={field.placeholder ?? ''}
      onChange={e => setDraft(e.target.value)}
      onKeyDown={handleKey}
      onClick={e => e.stopPropagation()}
    />
  );
}

export default DossierRow;
